import { useState, useEffect } from 'react';
import {
  CalendarCheck, ClipboardList, LayoutGrid, ScanLine, Ship, Handshake,
  BarChart3, CalendarRange, FileText, FileSpreadsheet, Settings as SettingsIcon,
  SlidersHorizontal, Anchor, ReceiptText, ShieldCheck, Globe, Wrench,
  LogOut, ChevronRight, ChevronsLeft, ChevronsRight, X, Inbox, Wallet, Hourglass,
  IndianRupee, HandCoins, CalendarDays,
} from 'lucide-react';
import './SideNav.css';

const STORAGE_KEY = 'alfresco-sidenav-collapsed';

// ─── Navigation model ─────────────────────────────────────────────────────────
// Leaves carry a `key` that matches an App screen key (onNavigate(key)).
// Parents carry a non-screen `key` (grp-*) so they never collide with a child
// screen key; a parent does not navigate — it only expands/collapses.
const NAV_ITEMS = [
  { key: 'view-bookings', label: 'Bookings',      icon: CalendarCheck },
  { key: 'events',        label: 'Events',        icon: CalendarDays },
  { key: 'daily-tracker', label: 'Daily Tracker', icon: ClipboardList },
  { key: 'seating-sheet', label: 'Seating Sheet', icon: LayoutGrid },
  { key: 'checkin',       label: 'Check In',      icon: ScanLine },
  { key: 'operations',    label: 'Operations',    icon: Ship },
  { key: 'partners',      label: 'Partners',      icon: Handshake },
  { key: 'partner-ledger', label: 'Partner Ledger', icon: HandCoins },
  { key: 'enquiries',     label: 'Enquiries',     icon: Inbox },
  { key: 'incomplete-bookings', label: 'Incomplete Bookings', icon: Hourglass },
  { key: 'payroll',       label: 'Payroll',       icon: Wallet },
  {
    key: 'grp-reports', label: 'Reports', icon: BarChart3,
    children: [
      { key: 'range-reports', label: 'Range Reports', icon: CalendarRange },
      { key: 'reports',       label: 'Daily Reports', icon: FileText },
      { key: 'collections-report', label: 'Collections', icon: IndianRupee },
      { key: 'invoice-export', label: 'Invoice',      icon: FileSpreadsheet },
    ],
  },
  {
    key: 'grp-settings', label: 'Settings', icon: SettingsIcon,
    children: [
      { key: 'settings',          label: 'General Settings',  icon: SlidersHorizontal },
      { key: 'cruise-settings',   label: 'Cruise Settings',   icon: Anchor },
      { key: 'invoice-settings',  label: 'Invoice Settings',  icon: ReceiptText },
      { key: 'security-settings', label: 'Security Settings', icon: ShieldCheck },
      { key: 'website-content',   label: 'Website Content',   icon: Globe },
      { key: 'maintenance',       label: 'Maintenance',       icon: Wrench },
    ],
  },
];

// The parent (if any) that owns a given screen key — drives auto-expand + the
// "contains active" highlight on a collapsed parent.
const parentOfScreen = (scr) =>
  NAV_ITEMS.find((it) => it.children && it.children.some((c) => c.key === scr));

export default function SideNav({ screen, onNavigate, onLogout, mobileOpen, onMobileClose }) {
  // Desktop rail collapse — persisted; mobile drawer ignores this (always full).
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [collapsed]);

  // Expanded parents (by parent key). Seed from the active screen so the parent
  // that owns the current screen starts open.
  const [expanded, setExpanded] = useState(() => {
    const p = parentOfScreen(screen);
    return p ? { [p.key]: true } : {};
  });
  // When the active screen changes, make sure its parent is open (without
  // collapsing any parent the user opened manually).
  useEffect(() => {
    const p = parentOfScreen(screen);
    if (p) setExpanded((prev) => (prev[p.key] ? prev : { ...prev, [p.key]: true }));
  }, [screen]);

  const toggleParent = (item) => {
    if (collapsed) {
      // In the rail, a parent icon can't show children in place — expand the
      // whole nav back to full width and open that parent.
      setCollapsed(false);
      setExpanded((prev) => ({ ...prev, [item.key]: true }));
      return;
    }
    setExpanded((prev) => ({ ...prev, [item.key]: !prev[item.key] }));
  };

  const renderLeaf = (item, depth = 0) => {
    const Icon = item.icon;
    const active = item.key === screen;
    return (
      <li key={item.key}>
        <button
          type="button"
          className={`sidenav-item${active ? ' active' : ''}${depth ? ' child' : ''}`}
          onClick={() => onNavigate(item.key)}
          aria-current={active ? 'page' : undefined}
        >
          <Icon className="sidenav-ico" size={18} strokeWidth={2} aria-hidden="true" />
          <span className="sidenav-label">{item.label}</span>
          <span className="sidenav-tooltip">{item.label}</span>
        </button>
      </li>
    );
  };

  const renderParent = (item) => {
    const Icon = item.icon;
    const isOpen = !!expanded[item.key];
    const containsActive = item.children.some((c) => c.key === screen);
    return (
      <li key={item.key}>
        <button
          type="button"
          className={`sidenav-item sidenav-parent${containsActive ? ' contains-active' : ''}`}
          onClick={() => toggleParent(item)}
          aria-expanded={isOpen}
        >
          <Icon className="sidenav-ico" size={18} strokeWidth={2} aria-hidden="true" />
          <span className="sidenav-label">{item.label}</span>
          <ChevronRight
            className={`sidenav-chevron${isOpen ? ' open' : ''}`}
            size={15}
            strokeWidth={2.25}
            aria-hidden="true"
          />
          <span className="sidenav-tooltip">{item.label}</span>
        </button>
        {isOpen && (
          <ul className="sidenav-children">
            {item.children.map((c) => renderLeaf(c, 1))}
          </ul>
        )}
      </li>
    );
  };

  return (
    <>
      <div
        className={`sidenav-backdrop${mobileOpen ? ' open' : ''}`}
        onClick={onMobileClose}
        aria-hidden="true"
      />
      <aside
        className={`sidenav${collapsed ? ' collapsed' : ''}${mobileOpen ? ' mobile-open' : ''}`}
        aria-label="Primary"
      >
        <div className="sidenav-mobile-head">
          <span className="sidenav-mobile-title">Menu</span>
          <button
            type="button"
            className="sidenav-mobile-close"
            onClick={onMobileClose}
            aria-label="Close navigation"
          >
            <X size={22} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>

        <div className="sidenav-head">
          <button
            type="button"
            className="sidenav-collapse-btn"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
            title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          >
            {collapsed
              ? <ChevronsRight size={18} strokeWidth={2} aria-hidden="true" />
              : <ChevronsLeft size={18} strokeWidth={2} aria-hidden="true" />}
          </button>
        </div>

        <nav className="sidenav-scroll">
          <ul className="sidenav-list">
            {NAV_ITEMS.map((item) => (item.children ? renderParent(item) : renderLeaf(item)))}
          </ul>
        </nav>

        <div className="sidenav-foot">
          <button
            type="button"
            className="sidenav-item sidenav-signout"
            onClick={onLogout}
          >
            <LogOut className="sidenav-ico" size={18} strokeWidth={2} aria-hidden="true" />
            <span className="sidenav-label">Sign out</span>
            <span className="sidenav-tooltip">Sign out</span>
          </button>
        </div>
      </aside>
    </>
  );
}
